import { useCallback, useMemo } from 'react';

import { useLDClient } from 'launchdarkly-react-client-sdk';

import { CONFIG } from '../../launch-darkly.config';
import { type LaunchDarklyContext } from '../../launch-darkly.types';
import { log } from '../../utils';
import { type SgLdClient } from './useSgLdClient.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useSgLdClient = (): SgLdClient => {
  const client = useLDClient();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isReady = Boolean(client);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const identify = useCallback(
    async (context: LaunchDarklyContext) => {
      if (!client) return;

      try {
        await withTimeout(client.identify(context), CONFIG.INIT_TIMEOUT_IN_SECONDS);
      } catch (error) {
        log.error(error);
      }
    },
    [client],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => ({ identify, isReady }), [identify, isReady]);
};

/**
 * In contrast to the React Native SDK, LD React SDK does not include a timeout
 * option for the identify method.
 *
 * This function can be used as a wrapper around the identify function that adds
 * a timeout.
 */
function withTimeout<T>(promise: Promise<T>, timeoutInSeconds: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            'Failed in identify provided context within timeout. Previous context will be used.',
          ),
        );
      }, timeoutInSeconds * 1000);
    }),
  ]);
}
